import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useSelector } from '@legendapp/state/react';
import { GripVertical } from 'lucide-react';
import { ui$ } from '../store/ui$';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { useRightPanelLayout } from '../hooks/useRightPanelLayout';
import { useScrollLock } from '../hooks/useScrollLock';
import { registerRightPanel } from '../lib/rightPanelRegistry';
import { AIHeaderAction } from './AIHeaderAction';
import './RightPanelShell.css';
import { ICON_SIZE, Tabs } from '../design-system';

/**
 * RightPanelShell — the one docked right-hand panel chrome for the whole app.
 *
 * Article, list/dashboard, and deck panels previously each carried their own copy of
 * the presence motion, --ai-panel-width side effect, resize handle, backdrop, and
 * hover scroll lock. They now pass a tab manifest to this shell and cascade-override
 * only what is genuinely view-specific.
 *
 * @param {string} props.panelId - DOM id; also the aria-controls target of the header action
 * @param {string} [props.className] - View-specific class for cascade overrides
 * @param {boolean} props.isOpen - Whether the panel should be present
 * @param {() => void} props.onClose
 * @param {Array<{id, label, icon, badge?}>} props.tabs
 * @param {string} props.activeTab
 * @param {(id: string) => void} props.onTabChange
 * @param {string} props.closeLabel - Accessible name for the close action
 * @param {React.ReactNode} props.children - Rendered inside the content surface
 */
const RightPanelShell = ({
  panelId,
  className = '',
  isOpen,
  onClose,
  tabs = [],
  activeTab,
  onTabChange,
  closeLabel,
  children,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const { isResizing, handleResizeStart } = useResizablePanel();
  const panelWidth = useSelector(() => ui$.aiPanelWidth.get()) || 340;
  const layout = useRightPanelLayout(panelWidth);

  const panelRef = useRef(null);
  const [isMounted, setIsMounted] = useState(isOpen);
  const [motionState, setMotionState] = useState('closed');

  useScrollLock(isOpen && isHovering);

  // Publish the open panel so the shell's content-width guard can reclaim its rail
  // without knowing where this particular panel keeps its open state.
  useEffect(() => {
    if (!isOpen) return undefined;
    return registerRightPanel({ close: onClose });
  }, [isOpen, onClose]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (isOpen) {
        setIsMounted(true);
        setMotionState('open');
        return;
      }

      if (panelRef.current?.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      setMotionState('closed');
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  // Keep shell classes and the root width publication in one pre-paint effect so
  // content reservation cannot disagree with the panel's geometry for a frame.
  //
  // Deliberately no dependency array. Layout owns `.app-container`'s className
  // and React rewrites that whole attribute on every shell re-render — route
  // changes included — which wipes classes applied only once, leaving the panel
  // open with no docked layout behind it. Re-asserting after each render (and
  // before paint) keeps these classes authoritative; unmount still cleans up.
  //
  // The width publication follows motionState on purpose: it must collapse the
  // moment the close starts so .app-main grows in step with the exiting panel
  // (its own 240ms drawer transition carries the animation). The header's
  // reserved lane guards itself against the resulting 0px with a max() — see
  // the docked rule in App.css.
  useLayoutEffect(() => {
    const container = document.querySelector('.app-container');
    const isDocked = layout === 'docked';

    container?.classList.toggle('right-panel-docked-motion', (isOpen || isMounted) && isDocked);
    container?.classList.toggle('right-panel-floating', isOpen && !isDocked);

    if (motionState === 'open' && isDocked) {
      document.documentElement.style.setProperty('--ai-panel-width', `${panelWidth}px`);
    } else {
      document.documentElement.style.setProperty('--ai-panel-width', '0px');
    }

    return () => {
      container?.classList.remove('right-panel-docked-motion', 'right-panel-floating');
      document.documentElement.style.setProperty('--ai-panel-width', '0px');
    };
  });

  const handlePanelTransitionEnd = (event) => {
    if (event.target !== panelRef.current) return;
    if (isOpen || motionState !== 'closed') return;
    if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
    setIsMounted(false);
  };

  if (!isOpen && !isMounted) return null;

  return (
    <>
      <div
        className="ai-chat-panel-backdrop right-panel-backdrop"
        data-state={motionState}
        data-layout={layout}
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        id={panelId}
        className={`ai-chat-panel right-panel ${className} ${isResizing ? 'is-resizing' : ''}`.trim()}
        data-state={motionState}
        data-layout={layout}
        aria-hidden={motionState !== 'open'}
        style={layout === 'docked' ? { width: `${panelWidth}px` } : undefined}
        ref={panelRef}
        onTransitionEnd={handlePanelTransitionEnd}
        onTransitionCancel={handlePanelTransitionEnd}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="ai-chat-resize-handle" onMouseDown={handleResizeStart}>
          <GripVertical size={ICON_SIZE.xxs} />
        </div>

        <div className="right-panel-tab-bar">
          <Tabs
            variant="segment"
            className="right-panel-tabs"
            ariaLabel={tabs.map((tab) => tab.label).join(', ')}
            value={activeTab}
            onValueChange={onTabChange}
            items={tabs.map((tab) => {
              const Icon = tab.icon;
              return {
                value: tab.id,
                id: `${panelId}-tab-${tab.id}`,
                // Only the active panel is mounted; avoid dangling references.
                controls: activeTab === tab.id ? `${panelId}-panel-${tab.id}` : undefined,
                label: tab.label,
                icon: Icon ? <><Icon size={ICON_SIZE.xs} />{tab.badge}</> : undefined,
              };
            })}
          />
          {isOpen && (
            <AIHeaderAction
              expanded
              className="right-panel-close"
              onClick={onClose}
              label={closeLabel}
              panelId={panelId}
            />
          )}
        </div>

        {/* Resolves the aria-controls the tabs point at. Keyed by tab so assistive
            tech sees a new panel on switch rather than mutated content. */}
        <div
          className="right-panel-content ai-panel-content-surface"
          id={`${panelId}-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`${panelId}-tab-${activeTab}`}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default RightPanelShell;
